import React, { useEffect, useState } from "react";
import { LeaveType } from "./LeaveTypeList";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<LeaveType, "id">, id?: number) => void;
  editData: LeaveType | null;
}

const LeaveTypeFormModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  editData,
}) => {
  const [typeName, setTypeName] = useState("");
  const [description, setDescription] = useState("");
  const [typicalUsage, setTypicalUsage] = useState("");
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const { t } = useTranslation();

  useEffect(() => {
    if (editData) {
      setTypeName(editData.typeName);
      setDescription(editData.description);
      setTypicalUsage(editData.typicalUsage);
    } else {
      setTypeName("");
      setDescription("");
      setTypicalUsage("");
    }
    setTouched({});
  }, [editData, open]);

  const validate = () => {
    const errors: { [key: string]: string } = {};
    if (!typeName.trim()) errors.typeName = t("TypeNameRequired");
    if (!typicalUsage.trim()) errors.typicalUsage = t("TypicalUsageRequired");
    return errors;
  };

  const errors = validate();
  const isInvalid = (field: string) => !!errors[field] && touched[field];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ typeName: true, typicalUsage: true });
    if (Object.keys(errors).length > 0) return;
    onSubmit({ typeName, description, typicalUsage }, editData?.id);
  };

  return (
    <Modal
      isOpen={open}
      toggle={onClose}
      className="modal-dialog modal-lg"
      centered
    >
      <ModalHeader toggle={onClose}>
        {editData ? t("EditLeaveType") : t("AddLeaveType")}
      </ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit} className="theme-form">
          <Row>
            <Col sm="12">
              <FormGroup>
                <Label>{t("Leave Type")} *</Label>
                <Input
                  value={typeName}
                  onChange={(e) => setTypeName(e.target.value)}
                  className="form-control"
                  invalid={isInvalid("typeName")}
                  required
                />
                <FormFeedback>{errors.typeName}</FormFeedback>
              </FormGroup>
            </Col>
            <Col sm="12">
              <FormGroup>
                <Label>{t("Description")}</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-control"
                  type="textarea"
                />
              </FormGroup>
            </Col>
            <Col sm="12">
              <FormGroup>
                <Label>{t("TypicalUsage")} *</Label>
                <Input
                  value={typicalUsage}
                  onChange={(e) => setTypicalUsage(e.target.value)}
                  className="form-control"
                  invalid={isInvalid("typicalUsage")}
                  required
                />
                <FormFeedback>{errors.typicalUsage}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>
          <div className="text-end mt-3">
            <Button
              color="secondary"
              className="me-2"
              type="button"
              onClick={onClose}
            >
              {t("Cancel")}
            </Button>
            <Button color="primary" type="submit">
              {editData ? t("Update") : t("Add")}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default LeaveTypeFormModal;
